/**
 * 공유 히스토리 로더(D2-6b) — 두 패널이 각자 onLoadMore 를 받을 수 있게
 * 되면서(D2-6, v1 의 "좌측 전용" 철회) 생긴 중복 호출을 없앤다.
 *
 * 동시 호출은 같은 in-flight 프라미스를 돌려받고, `false` 를 한 번 받으면
 * `exhausted` 를 세워 이후 요청은 즉답한다. 인스턴스 로컬 `exhausted`
 * (`caption-feed.tsx:212`)는 그대로 두고 덮어쓰지 않는다 — 각자의
 * `loadingMore` 스피너 표시는 계속 자기 패널에서 돈다.
 */

import { useCallback, useRef, useState } from 'react'

export function useSharedLoadMore(onLoadMore?: () => Promise<boolean>): {
  /** onLoadMore 가 없으면 undefined(= clearMark 경로). */
  request: (() => Promise<boolean>) | undefined
  exhausted: boolean
  loading: boolean
} {
  const [exhausted, setExhausted] = useState(false)
  const [loading, setLoading] = useState(false)
  const inFlightRef = useRef<Promise<boolean> | null>(null)
  const exhaustedRef = useRef(false)

  const request = useCallback((): Promise<boolean> => {
    if (exhaustedRef.current) return Promise.resolve(false)
    if (inFlightRef.current) return inFlightRef.current
    if (!onLoadMore) return Promise.resolve(false)

    setLoading(true)
    const promise = onLoadMore()
      .then((more) => {
        if (!more) {
          exhaustedRef.current = true
          setExhausted(true)
        }
        return more
      })
      .catch(() => {
        exhaustedRef.current = true
        setExhausted(true)
        return false
      })
      .finally(() => {
        inFlightRef.current = null
        setLoading(false)
      })
    inFlightRef.current = promise
    return promise
  }, [onLoadMore])

  return { request: onLoadMore ? request : undefined, exhausted, loading }
}
